import { useEffect, useMemo, useRef } from 'react';

import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useNavigate, useSearch } from '@tanstack/react-router';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { isEmpty } from 'es-toolkit/compat';

import EfficiencyInsightsTable from './EfficiencyInsightsTable';
import {
  getDefaultEfficiencyCategory,
  getEfficiencyCategoriesForPlatform,
  getEfficiencyCategoryLabel,
  getStarvationBidderPlatformConfig,
  isValidEfficiencyCategoryForPlatform,
} from '../../common';
import { FilterDimension } from '../../schemas';
import {
  setAdGroupFilter,
  starvationBidderAdgroupFilterSelector,
} from '../../slice';

import {
  useGetStarvationBidderAdgroupsQuery,
  useGetStarvationInsightsMetricsQuery,
} from '@/api/endpoints/starvationBidder';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { platformTerminologies, usePlatform } from '@/features/users';
import { AsyncAutocompleteTextfield } from '@/shared/components/autocomplete';
import { useGlobalFilterKeys } from '@/shared/hooks';

const toTitleCase = (value: string) =>
  value.replace(/\b\w/g, (char) => char.toUpperCase());

const InsightsExplorer = () => {
  const platform = usePlatform();
  const platformConfig = useMemo(
    () => getStarvationBidderPlatformConfig(platform),
    [platform]
  );
  const adUnitLabel = toTitleCase(
    platformTerminologies.adUnit[platform].singular
  );

  const navigate = useNavigate({
    from: '/algorithm-tools/starvation-bidder',
  });
  const {
    efficiencyCategory,
    adGroup: adGroupUrlParam,
    effPage,
    effPageSize,
    efficiencyInsightsOrdering,
  } = useSearch({
    from: '/_app/algorithm-tools/starvation-bidder',
  });

  const dispatch = useAppDispatch();
  const appliedAdgroup = useAppSelector(starvationBidderAdgroupFilterSelector);
  const areUrlFiltersValidated = useRef(false);
  const firstRunUrlParams = useRef(true);

  const categoryOptions = useMemo(
    () => getEfficiencyCategoriesForPlatform(platform),
    [platform]
  );

  const resolvedEfficiencyCategory = useMemo(() => {
    if (isValidEfficiencyCategoryForPlatform(efficiencyCategory, platform)) {
      return efficiencyCategory;
    }
    return getDefaultEfficiencyCategory(platform);
  }, [efficiencyCategory, platform]);

  const { globalFilterKeys, isGlobalFilterReady } = useGlobalFilterKeys();
  const {
    data: adGroups,
    isLoading: isLoadingAdgroups,
    isFetching: isFetchingAdgroups,
    isError: isErrorAdgroups,
    refetch: refetchAdgroups,
  } = useGetStarvationBidderAdgroupsQuery(globalFilterKeys, {
    skip: !isGlobalFilterReady,
  });

  const adGroupsOptions = useMemo(() => {
    if (!adGroups) {
      return [];
    }

    return adGroups.map((adGroup) => ({
      id: adGroup.id,
      nameAndId: `${adGroup.adGroupName} (${adGroup.id})`,
      highlightedParts: undefined as
        | { text: string; highlight: boolean }[]
        | undefined,
    }));
  }, [adGroups]);

  const {
    data: insightsMetrics,
    isLoading,
    isFetching,
    isError,
  } = useGetStarvationInsightsMetricsQuery(
    {
      filterDimension: resolvedEfficiencyCategory,
      [platformConfig.insightsAdUnitIdParamKey]: appliedAdgroup?.id ?? '',
      platform,
      page: effPage,
      pageSize: effPageSize,
      ordering: efficiencyInsightsOrdering,
      ...globalFilterKeys,
    },
    {
      skip:
        !areUrlFiltersValidated.current ||
        !appliedAdgroup?.id ||
        !isGlobalFilterReady,
    }
  );

  useEffect(() => {
    if (efficiencyCategory === resolvedEfficiencyCategory) {
      return;
    }

    void navigate({
      search: (prev) => ({
        ...prev,
        effPage: 1,
        efficiencyCategory: resolvedEfficiencyCategory,
      }),
      resetScroll: false,
    });
  }, [efficiencyCategory, navigate, resolvedEfficiencyCategory]);

  const handleAdgroupChange = (newAdgroup: typeof appliedAdgroup) => {
    if (!newAdgroup || newAdgroup.id === appliedAdgroup?.id) {
      return;
    }

    dispatch(setAdGroupFilter(newAdgroup));
  };

  // navigation
  useEffect(() => {
    if (!areUrlFiltersValidated.current) {
      return;
    }

    void navigate({
      search: (prev) => ({
        ...prev,
        effPage: 1,
        adGroup: appliedAdgroup?.id,
      }),
      resetScroll: false,
    });
  }, [appliedAdgroup?.id, navigate]);

  // reset validation if URL params are removed
  // skip on first run
  useEffect(() => {
    if (firstRunUrlParams.current) {
      firstRunUrlParams.current = false;
      return;
    }

    if (!adGroupUrlParam) {
      areUrlFiltersValidated.current = false;
    }
  }, [adGroupUrlParam]);

  // Validate URL filters on first run or auto select the first entries
  useEffect(() => {
    if (
      !isGlobalFilterReady ||
      isFetchingAdgroups ||
      areUrlFiltersValidated.current
    ) {
      return;
    }

    if (isEmpty(adGroupsOptions)) {
      dispatch(setAdGroupFilter(null));
      return;
    }

    // validate adGroup filter
    const adGroupOption =
      adGroupsOptions.find((option) => option.id === adGroupUrlParam) ??
      adGroupsOptions[0];
    dispatch(setAdGroupFilter(adGroupOption));

    areUrlFiltersValidated.current = true;
  }, [
    adGroupUrlParam,
    adGroupsOptions,
    dispatch,
    isGlobalFilterReady,
    isFetchingAdgroups,
  ]);

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: (theme) => theme.customShadows.sm,
      }}
    >
      <Stack
        direction='row'
        px={3}
        py={2}
        gap={4}
        alignItems='flex-end'
        justifyContent='space-between'
      >
        <Box>
          <Typography variant='h6' component='h2'>
            Efficiency Insights
          </Typography>
          <Typography variant='body2' color='text.secondary' pt={0.5}>
            Files and assets that have been attached to this project.
          </Typography>
        </Box>

        <Stack direction='row' gap={1}>
          <Autocomplete
            id='adGroup'
            size='small'
            loading={isFetchingAdgroups}
            renderInput={(params) => (
              <AsyncAutocompleteTextfield
                {...params}
                label={adUnitLabel}
                placeholderText={`Select ${adUnitLabel}`}
                isFetching={isFetchingAdgroups}
                isError={isErrorAdgroups}
                handleRefetch={() => {
                  void refetchAdgroups();
                }}
                sx={{ minWidth: 300 }}
              />
            )}
            options={adGroupsOptions}
            getOptionLabel={(option) => option.nameAndId}
            value={appliedAdgroup}
            onChange={(_event, newValue) => {
              handleAdgroupChange(newValue);
            }}
            slotProps={{
              listbox: {
                sx: {
                  wordBreak: 'break-word',
                },
              },
            }}
            sx={{
              [`& .${autocompleteClasses.clearIndicator}`]: {
                display: 'none',
              },
            }}
            filterOptions={(options, state) => {
              const inputTerm = state.inputValue.trim().toLowerCase();

              if (!inputTerm) {
                return options;
              }

              const filteredOpts: typeof options = [];

              for (const opt of options) {
                const matches = match(opt.nameAndId, inputTerm, {
                  insideWords: true,
                });

                if (!matches.length) {
                  continue;
                }

                const highlightedParts = parse(opt.nameAndId, matches);
                filteredOpts.push({
                  ...opt,
                  highlightedParts,
                });
              }

              return filteredOpts;
            }}
            renderOption={(
              props: React.HTMLAttributes<HTMLLIElement>,
              option
            ) => {
              const { key, ...otherProps } = props as {
                key: React.Key;
              } & React.HTMLAttributes<HTMLLIElement>;

              if (option.highlightedParts?.length) {
                return (
                  <li key={key} {...otherProps}>
                    <span>
                      {option.highlightedParts.map((value, index) =>
                        value.highlight ? (
                          <mark key={index}>{value.text}</mark>
                        ) : (
                          value.text
                        )
                      )}
                    </span>
                  </li>
                );
              }

              return (
                <li key={key} {...otherProps}>
                  {option.nameAndId}
                </li>
              );
            }}
          />

          <Autocomplete
            disableClearable
            size='small'
            options={categoryOptions}
            value={resolvedEfficiencyCategory}
            getOptionLabel={(option: FilterDimension) =>
              getEfficiencyCategoryLabel(option, platform)
            }
            onChange={(_e, value) => {
              void navigate({
                search: (old) => ({
                  ...old,
                  effPage: 1,
                  efficiencyCategory: value,
                }),
                resetScroll: true,
              });
            }}
            renderInput={(params) => (
              <TextField {...params} label='Category' sx={{ minWidth: 200 }} />
            )}
          />
        </Stack>
      </Stack>

      <Divider />

      <EfficiencyInsightsTable
        isLoading={isLoading || isLoadingAdgroups}
        isFetching={isFetching || isFetchingAdgroups}
        isError={isError || isErrorAdgroups}
        data={
          adGroupsOptions.length && insightsMetrics?.results
            ? insightsMetrics.results
            : []
        }
        dataCount={
          adGroupsOptions.length && insightsMetrics?.count
            ? insightsMetrics.count
            : 0
        }
        efficiencyCategory={resolvedEfficiencyCategory}
      />
    </Paper>
  );
};

export default InsightsExplorer;
